import { useEffect, useMemo } from 'react';
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { resources } from "@constants/hardcoded-strings";
import {
    MAX_PIN_SEND_COUNT_PROPERTY_TYPE_ID,
    MAX_PIN_VERIFY_COUNT_PROPERTY_TYPE_ID,
    MAX_VERIFICATION_COUNT_PROPERTY_TYPE_ID,
    PIN_TIMEOUT_PROPERTY_TYPE_ID,
    HARDCODED_PINS_PROPERTY_TYPE_ID,
    PIN_CODE_LENGTH,
    DEFAULT_PIN_SEND_COUNT,
    DEFAULT_VERIFICATION_COUNT,
    DEFAULT_PIN_TIMEOUT,
    MOCK_SEND_EMAIL_PIN_PROPERTY_TYPE_ID
} from '@constants/pin-code-constants';
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { RadioInputFormGroup } from "@containers/common/radio-input-form-group/radio-input-form-group";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { GeneralAdvancedValidationErrors } from "@idw-types/common/validation/common/general-advanced-validation-errors";
import { FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { FormattedAppliedProfile } from '@idw-types/data/applied-profile/applied-profile';
import { getPinCodeProperties } from "@utils/pin-code/pin-code-utils";
import { isNullOrUndefined } from "@utils/object-util";
import { isStringValidNumberRegex } from "@utils/string-util";
import { PropertiesSectionDescription } from '../properties-section-header/properties-section-description';
import { ContentPageHeader } from '../../content-page-header/content-page-header';
import { ContentPageBody } from '../../content-page-body/content-page-body';
import styles from "./pin-code-container.less";
import { AxnDocumentationLink } from '@idw-types/data/reference-data/axn/axn-documentation-link';

type PinCodeContainerProps = {
    isDisabled: boolean;
    validationErrors: GeneralAdvancedValidationErrors;
    showSuppressEmailPinInput: boolean;
    properties: Array<FormattedRpServiceConfigurationProperty>;
    appliedProfiles: Array<FormattedAppliedProfile>;
    documentationLinks: Array<AxnDocumentationLink>;
    updateProperties: (properties: Array<{ propertyTypeId: string, newValue: string, added: boolean }>) => void;
};

export const PinCodeContainer = (props: PinCodeContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        showSuppressEmailPinInput,
        properties,
        appliedProfiles,
        documentationLinks,
        updateProperties
    } = props;

    const pinCodeResources = resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.pinCode;

    const onChangeMaxPinSendCountInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = parseInt(event.target.value);
        let newMaxPinSendCount = "";
        let newMaxVerificationCount = "";
        if (!isNaN(newValue)) {
            newMaxPinSendCount = `${newValue}`;
            newMaxVerificationCount = `${newValue + 1}`;
        }
        updateProperties([
            { propertyTypeId: MAX_PIN_SEND_COUNT_PROPERTY_TYPE_ID, newValue: newMaxPinSendCount, added: true },
            { propertyTypeId: MAX_PIN_VERIFY_COUNT_PROPERTY_TYPE_ID, newValue: newMaxPinSendCount, added: true },
            { propertyTypeId: MAX_VERIFICATION_COUNT_PROPERTY_TYPE_ID, newValue: newMaxVerificationCount, added: true },
        ]);
    };

    const onChangePinTimeoutInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const timeoutInMinutes = parseInt(event.target.value);
        let timeoutInSeconds = "";
        if (!isNaN(timeoutInMinutes)) {
            timeoutInSeconds = `${timeoutInMinutes * 60}`;
        }
        updateProperties([
            { propertyTypeId: PIN_TIMEOUT_PROPERTY_TYPE_ID, newValue: timeoutInSeconds, added: true }
        ]);
    };

    const onChangeHardcodedPinInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (isStringValidNumberRegex(event.target.value) && event.target.value.length <= PIN_CODE_LENGTH) {
            updateProperties([
                { propertyTypeId: HARDCODED_PINS_PROPERTY_TYPE_ID, newValue: event.target.value, added: true }
            ]);
        }
    };

    const {
        maxPinSendCount,
        maxPinVerifyCount,
        maxVerificationCount,
        pinTimeout,
        hardcodedPinCode,
        mockSendEmailPin
    } = useMemo(() => {
        return getPinCodeProperties(properties, appliedProfiles);
    }, [properties]);

    const onChangeEnableHardcodedPin = (newValue: string) => {
        if (isStringTrue(newValue)) {
            updateProperties([
                { propertyTypeId: HARDCODED_PINS_PROPERTY_TYPE_ID, newValue: "123456", added: true }
            ]);
        } else {
            updateProperties([
                { propertyTypeId: HARDCODED_PINS_PROPERTY_TYPE_ID, newValue: null, added: false }
            ]);
        }
    };

    const onChangeEnableMockSendEmailPin = (newValue: string) => {
        if (isStringTrue(newValue)) {
            updateProperties([{ propertyTypeId: MOCK_SEND_EMAIL_PIN_PROPERTY_TYPE_ID, newValue: "true", added: true }]);
        } else {
            updateProperties([{ propertyTypeId: MOCK_SEND_EMAIL_PIN_PROPERTY_TYPE_ID, newValue: null, added: false }]);
        }
    };

    const isPinCodeConfigurationEnabled = useMemo(() => {
        return (
            !isNullOrUndefined(maxPinSendCount) &&
            !isNullOrUndefined(maxPinVerifyCount) &&
            !isNullOrUndefined(maxVerificationCount) &&
            !isNullOrUndefined(pinTimeout)
        );
    }, [maxPinSendCount, maxPinVerifyCount, maxVerificationCount, pinTimeout]);

    const isHardcodedPinEnabled = useMemo(() => {
        return !isNullOrUndefined(hardcodedPinCode);
    }, [hardcodedPinCode]);

    const isMockSendEmailPinEnabled = useMemo(() => {
        return !isNullOrUndefined(mockSendEmailPin) && isStringTrue(mockSendEmailPin);
    }, [mockSendEmailPin]);

    useEffect(() => {
        if (!isPinCodeConfigurationEnabled && !isDisabled) {
            const propertiesToChange: Array<{ propertyTypeId: string; newValue: string; added: boolean; }> = [];
            if (isNullOrUndefined(maxPinSendCount)) {
                propertiesToChange.push({ propertyTypeId: MAX_PIN_SEND_COUNT_PROPERTY_TYPE_ID, newValue: DEFAULT_PIN_SEND_COUNT, added: true });
            }
            if (isNullOrUndefined(maxPinVerifyCount)) {
                propertiesToChange.push({ propertyTypeId: MAX_PIN_VERIFY_COUNT_PROPERTY_TYPE_ID, newValue: DEFAULT_PIN_SEND_COUNT, added: true });
            }
            if (isNullOrUndefined(maxVerificationCount)) {
                propertiesToChange.push({ propertyTypeId: MAX_VERIFICATION_COUNT_PROPERTY_TYPE_ID, newValue: DEFAULT_VERIFICATION_COUNT, added: true });
            }
            if (isNullOrUndefined(pinTimeout)) {
                propertiesToChange.push({ propertyTypeId: PIN_TIMEOUT_PROPERTY_TYPE_ID, newValue: DEFAULT_PIN_TIMEOUT, added: true });
            }
            updateProperties(propertiesToChange);
        }
    }, [isPinCodeConfigurationEnabled, isDisabled]);

    const documentationLink = useMemo(() => {
        if (documentationLinks.length) {
            const documentationLink = documentationLinks.find(doc => doc.shortName === 'pin-code-settings-widgets');
            if (documentationLink) {
                return documentationLink.url;
            }
        }

        return '';
    }, [documentationLinks]);
    
    return (
        <div>
            <ContentPageHeader title={pinCodeResources.title} />
            <ContentPageBody isMaxWidth={true}>
                <PropertiesSectionDescription description={pinCodeResources.description}  documentationLink={documentationLink} />
                <InputFormGroup
                    controlId={'max-pin-send-count-input'}
                    automationId={'max-pin-send-count-input'}
                    label={pinCodeResources.inputs.pinResendAndValidationAttempts.label}
                    ariaLabel={pinCodeResources.inputs.pinResendAndValidationAttempts.label}
                    onChange={onChangeMaxPinSendCountInput}
                    value={maxPinSendCount || ''}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={LayoutTypes.ROW}
                    labelWidth={LabelWidthTypes.LARGE}
                    afterLabelTooltipText={pinCodeResources.inputs.pinResendAndValidationAttempts.tooltip}
                />
                {validationErrors.pinResendAndValidationAttemptsError}
                <InputFormGroup
                    controlId={'pin-timeout-input'}
                    automationId={'pin-timeout-input'}
                    label={pinCodeResources.inputs.pinTimeout.label}
                    ariaLabel={pinCodeResources.inputs.pinTimeout.label}
                    onChange={onChangePinTimeoutInput}
                    value={pinTimeout ? `${parseInt(pinTimeout) / 60}` : ""}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={LayoutTypes.ROW}
                    labelWidth={LabelWidthTypes.LARGE}
                    afterLabelTooltipText={pinCodeResources.inputs.pinTimeout.tooltip}
                />
                {validationErrors.pinTimeoutError}
                <RadioInputFormGroup
                    controlId={'enable-hardcoded-pin'}
                    automationId={'enable-hardcoded-pin'}
                    disabled={isDisabled}
                    isRequired={true}
                    label={pinCodeResources.inputs.generateStaticPinCode.label}
                    labelWidth={LabelWidthTypes.LARGE}
                    value={isHardcodedPinEnabled ? BOOLEAN_RADIO_INPUT_OPTIONS[0].value : BOOLEAN_RADIO_INPUT_OPTIONS[1].value}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    onChange={onChangeEnableHardcodedPin}
                    afterLabelTooltipText={pinCodeResources.inputs.generateStaticPinCode.tooltip}
                />
                {isHardcodedPinEnabled && (
                    <>
                        <InputFormGroup
                            controlId={'hardcoded-pin-input'}
                            automationId={'hardcoded-pin-input'}
                            className={styles.staticPinCodeContainer}
                            label={pinCodeResources.inputs.staticPinCode.label}
                            ariaLabel={pinCodeResources.inputs.staticPinCode.label}
                            onChange={onChangeHardcodedPinInput}
                            value={hardcodedPinCode || ''}
                            disabled={isDisabled}
                            isRequired={true}
                            layout={LayoutTypes.ROW}
                            labelWidth={LabelWidthTypes.LARGE}
                            afterLabelTooltipText={pinCodeResources.inputs.staticPinCode.tooltip}
                        />
                        {validationErrors.staticPinCodeError}
                    </>
                )}
                {showSuppressEmailPinInput && (
                    <RadioInputFormGroup
                        controlId={'mock-send-email-pin'}
                        automationId={'mock-send-email-pin'}
                        disabled={isDisabled}
                        isRequired={true}
                        label={pinCodeResources.inputs.mockSendEmailPin.label}
                        labelWidth={LabelWidthTypes.LARGE}
                        value={isMockSendEmailPinEnabled ? BOOLEAN_RADIO_INPUT_OPTIONS[0].value : BOOLEAN_RADIO_INPUT_OPTIONS[1].value}
                        options={BOOLEAN_RADIO_INPUT_OPTIONS}
                        onChange={onChangeEnableMockSendEmailPin}
                        afterLabelTooltipText={pinCodeResources.inputs.mockSendEmailPin.tooltip}
                    />
                )}
            </ContentPageBody>
        </div>
    );
};